import React from "react";
import { Select, SelectProps } from "@mantine/core";
import { AppIcon } from "../AppIcon";

const RECURRENCE_OPTIONS = [
  {
    value: "NONE",
    label: "No Recurrence",
    icon: "X",
    color: "var(--mantine-color-green-6)",
    description: "",
  },
  {
    value: "WEEKLY",
    label: "Weekly",
    icon: "Refresh",
    color: "var(--mantine-color-green-6)",
    description: "Repeats on specific days of the week",
  },
  {
    value: "MONTHLY",
    label: "Monthly",
    icon: "Refresh",
    color: "var(--mantine-color-green-6)",
    description: "Repeats on specific days of the month",
  },
] as const;

export interface RecurrenceSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "required"> {
  /** Current status value (100 for enabled, 0 for disabled) */
  value: string | null | undefined;

  /** Callback when status changes */
  onChange: (value: string | null) => void;

  /** Whether to show icons in the dropdown */
  withIcons?: boolean;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;

  required?: boolean;
}

export const RecurrenceSelector: React.FC<RecurrenceSelectorProps> = ({
  value,
  onChange,
  withIcons = true,
  withDescriptions = false,
  placeholder = "Select recurrence",
  required,
  ...selectProps
}) => {
  // Convert numeric value to string for Select component
  const stringValue = value !== null ? String(value) : null;

  const recurrenceOptions =
    required === true
      ? RECURRENCE_OPTIONS.filter((option) => option.value != "NONE")
      : RECURRENCE_OPTIONS;

  // Prepare select data with optional descriptions
  const selectData = recurrenceOptions.map((option) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
    };
  });

  // Handle change and convert back to number
  const handleChange = (stringValue: string | null) => {
    if (stringValue === null) {
      onChange(null);
    } else {
      onChange(stringValue);
    }
  };

  // Get current option for icon display
  const currentOption = RECURRENCE_OPTIONS.find(
    (opt) => opt.value === stringValue
  );

  const leftSection =
    withIcons && currentOption ? (
      <AppIcon
        iconName={currentOption.icon as any}
        size={16}
        style={{
          color: currentOption.color,
        }}
      />
    ) : undefined;

  return (
    <Select
      {...selectProps}
      data={selectData}
      value={stringValue}
      onChange={handleChange}
      placeholder={placeholder}
      leftSection={leftSection}
      clearable={false}
      size="md"
    />
  );
};

// Export status options for use in other components
export { RECURRENCE_OPTIONS };
